import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../../context/ToastContext';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  VideoCameraIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { firestore } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { DeleteConfirmation } from '../../ui/DeleteConfirmation';
import CreateSession from './CreateSession';

const SessionCalendar = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const handleDeleteClick = session => {
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'sessions', sessionToDelete.id));
      toast.success('Session deleted successfully!');
      setShowDeleteModal(false);
      setSessionToDelete(null);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to delete session', 'Error');
    }
  };

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let unsubscribe = null;
    let fallbackUnsubscribe = null;

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = currentUser.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || currentUser.email;
      } catch (e) {
        // Invalid JSON, use current user email
      }
    }

    // Try query with orderBy first (requires composite index)
    const sessionsQuery = query(
      collection(firestore, 'sessions'),
      where('coachEmail', '==', effectiveEmail),
      orderBy('date', 'asc')
    );

    unsubscribe = onSnapshot(
      sessionsQuery,
      snapshot => {
        const sessionsData = snapshot.docs.map(doc => {
          const data = doc.data();
          let dateStr = '';
          if (data.date) {
            if (typeof data.date === 'string') {
              dateStr = data.date.split('T')[0];
            } else if (data.date.toDate) {
              const dateObj = data.date.toDate();
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              dateStr = `${year}-${month}-${day}`;
            } else if (data.date instanceof Date) {
              // Date object - use local date components
              const year = data.date.getFullYear();
              const month = String(data.date.getMonth() + 1).padStart(2, '0');
              const day = String(data.date.getDate()).padStart(2, '0');
              dateStr = `${year}-${month}-${day}`;
            }
          }
          const session = {
            id: doc.id,
            client: data.clientName || 'Unknown Client',
            clientId: data.clientId || '',
            clientEmail: data.clientEmail || '',
            date: dateStr,
            time: data.time || 'TBD',
            duration: data.duration || '60',
            type: data.type || 'strategy',
            status: data.status || 'scheduled',
            location: data.location || 'online',
            meetingLink: data.meetingLink || '',
            notes: data.notes || '',
            ...data,
          };
          return session;
        });
        setSessions(sessionsData);
        setLoading(false);
      },
      error => {
        const delegatedUserData = localStorage.getItem('delegatedUser');
        let effectiveEmail = currentUser.email;

        if (delegatedUserData) {
          try {
            const parsed = JSON.parse(delegatedUserData);
            effectiveEmail = parsed.coachEmail || currentUser.email;
          } catch (e) {}
        }

        const simpleQuery = query(
          collection(firestore, 'sessions'),
          where('coachEmail', '==', effectiveEmail)
        );

        fallbackUnsubscribe = onSnapshot(
          simpleQuery,
          snapshot => {
            const sessionsData = snapshot.docs
              .map(doc => {
                const data = doc.data();
                let dateStr = '';
                if (data.date) {
                  if (typeof data.date === 'string') {
                    dateStr = data.date.split('T')[0]; // Handle ISO strings
                  } else if (data.date.toDate) {
                    const dateObj = data.date.toDate();
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    dateStr = `${year}-${month}-${day}`;
                  } else if (data.date instanceof Date) {
                    const year = data.date.getFullYear();
                    const month = String(data.date.getMonth() + 1).padStart(2, '0');
                    const day = String(data.date.getDate()).padStart(2, '0');
                    dateStr = `${year}-${month}-${day}`;
                  }
                }
                return {
                  id: doc.id,
                  client: data.clientName || 'Unknown Client',
                  clientId: data.clientId || '',
                  clientEmail: data.clientEmail || '',
                  date: dateStr,
                  time: data.time || 'TBD',
                  duration: data.duration || '60',
                  type: data.type || 'strategy',
                  status: data.status || 'scheduled',
                  location: data.location || 'online',
                  meetingLink: data.meetingLink || '',
                  notes: data.notes || '',
                  ...data,
                };
              })
              .sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                return dateA - dateB;
              });
            setSessions(sessionsData);
            setLoading(false);
          },
          fallbackError => {
            toast.error('Failed to fetch sessions', 'Error');
            setLoading(false);
          }
        );
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
      if (fallbackUnsubscribe) fallbackUnsubscribe();
    };
  }, [currentUser?.email, refreshKey]);

  const getSessionsForDate = date => {
    if (!date) return [];

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return sessions.filter(session => {
      if (!session.date) return false;
      // Handle string dates (YYYY-MM-DD)
      if (typeof session.date === 'string') {
        const sessionDateStr = session.date.split('T')[0];
        return sessionDateStr === dateStr;
      }
      // Handle Firestore Timestamp or Date objects
      if (session.date.toDate) {
        const sessionDate = session.date.toDate();
        const sessionYear = sessionDate.getFullYear();
        const sessionMonth = String(sessionDate.getMonth() + 1).padStart(2, '0');
        const sessionDay = String(sessionDate.getDate()).padStart(2, '0');
        const sessionDateStr = `${sessionYear}-${sessionMonth}-${sessionDay}`;
        return sessionDateStr === dateStr;
      }
      if (session.date instanceof Date) {
        const sessionYear = session.date.getFullYear();
        const sessionMonth = String(session.date.getMonth() + 1).padStart(2, '0');
        const sessionDay = String(session.date.getDate()).padStart(2, '0');
        const sessionDateStr = `${sessionYear}-${sessionMonth}-${sessionDay}`;
        return sessionDateStr === dateStr;
      }
      return false;
    });
  };

  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = date => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = date => {
    if (!date || !selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading calendar...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  const selectedDateSessions = selectedDate ? getSessionsForDate(selectedDate) : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg sm:p-6"
    >
      <div className="mb-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
            <CalendarIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-black">Session Calendar</h2>
            <p className="text-xs text-gray-500">
              {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} scheduled
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setEditingSession(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
          >
            <CalendarIcon className="h-5 w-5" />
            Create Session
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToToday}
            className="rounded-lg border border-black/20 px-3 py-1.5 text-sm text-black transition-colors hover:bg-black/5"
          >
            Today
          </motion.button>
          <div className="flex items-center gap-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goToPreviousMonth}
              className="rounded-lg p-1.5 hover:bg-gray-100"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </motion.button>
            <span className="min-w-[140px] text-center text-sm font-medium text-black">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goToNextMonth}
              className="rounded-lg p-1.5 hover:bg-gray-100"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="mb-2 grid grid-cols-7 gap-1">
            {dayNames.map(day => (
              <div key={day} className="p-2 text-center text-xs font-medium text-gray-600">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              const daySessions = day ? getSessionsForDate(day) : [];
              const hasSessions = daySessions.length > 0;

              return (
                <motion.button
                  key={index}
                  whileHover={day ? { scale: 1.05 } : {}}
                  whileTap={day ? { scale: 0.95 } : {}}
                  onClick={() => day && setSelectedDate(day)}
                  disabled={!day}
                  className={`relative min-h-[80px] rounded-lg border-2 p-2 text-left transition-all ${
                    !day
                      ? 'border-transparent'
                      : isToday(day)
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : isSelected(day)
                          ? 'border-blue-600 bg-blue-100 shadow-md'
                          : hasSessions
                            ? 'border-green-300 bg-green-50/50 hover:bg-green-100 hover:shadow-sm'
                            : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {day && (
                    <>
                      <span
                        className={`text-sm font-medium ${
                          isToday(day)
                            ? 'text-blue-600'
                            : isSelected(day)
                              ? 'text-blue-700'
                              : 'text-black'
                        }`}
                      >
                        {day.getDate()}
                      </span>
                      {hasSessions && (
                        <div className="mt-1.5 flex flex-col gap-0.5">
                          {daySessions.slice(0, 3).map((session, idx) => (
                            <div
                              key={session.id}
                              className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${
                                session.status === 'completed'
                                  ? 'bg-green-100 text-green-700'
                                  : session.status === 'cancelled'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-blue-100 text-blue-700'
                              }`}
                              title={`${session.time || 'TBD'} - ${session.client}`}
                            >
                              {session.time ? session.time.substring(0, 5) : 'TBD'}
                            </div>
                          ))}
                          {daySessions.length > 3 && (
                            <div className="rounded bg-gray-100 px-1 py-0.5 text-[10px] font-semibold text-gray-700">
                              +{daySessions.length - 3} more
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-5 shadow-sm"
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-black">
                Sessions on{' '}
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </h3>
              <p className="text-xs text-gray-500">
                {selectedDateSessions.length}{' '}
                {selectedDateSessions.length === 1 ? 'session' : 'sessions'}
              </p>
            </div>
            <button
              onClick={() => setSelectedDate(null)}
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {selectedDateSessions.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 bg-white p-8 text-center">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm font-medium text-gray-600">No sessions scheduled</p>
              <p className="mt-1 text-xs text-gray-500">Click "Create Session" to schedule one</p>
            </div>
          ) : (
            <div className="space-y-3">
              {selectedDateSessions.map(session => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600">
                          <UserIcon className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-black">{session.client}</span>
                          <div className="mt-0.5">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                session.status === 'scheduled'
                                  ? 'bg-blue-50 text-blue-700'
                                  : session.status === 'completed'
                                    ? 'bg-green-50 text-green-700'
                                    : session.status === 'cancelled'
                                      ? 'bg-red-50 text-red-700'
                                      : 'bg-gray-50 text-gray-700'
                              }`}
                            >
                              {session.status || 'scheduled'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{session.time || 'TBD'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span>{session.duration || '60'} min</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <TagIcon className="h-4 w-4 text-gray-400" />
                          <span className="capitalize">{session.type || 'strategy'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {session.location === 'online' ? (
                            <VideoCameraIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <MapPinIcon className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="capitalize">{session.location || 'online'}</span>
                        </div>
                      </div>
                      {session.notes && (
                        <div className="mb-3 rounded-lg bg-gray-50 p-2">
                          <p className="line-clamp-3 text-xs text-gray-700">{session.notes}</p>
                        </div>
                      )}
                      {session.meetingLink && session.location === 'online' && (
                        <a
                          href={session.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
                        >
                          <VideoCameraIcon className="h-4 w-4" />
                          Join Meeting
                        </a>
                      )}
                      <div className="mt-3 flex items-center gap-2 border-t pt-3">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditingSession(session);
                            setShowModal(true);
                          }}
                          className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700"
                          aria-label="Edit session"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSessionToDelete(session);
                            setShowDeleteModal(true);
                          }}
                          className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-red-500 hover:bg-red-50 hover:text-red-700"
                          aria-label="Delete session"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          Delete
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}
      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSessionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Session"
        message="Are you sure you want to delete this session? This action cannot be undone."
        itemName={sessionToDelete?.client}
      />

      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSessionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Session"
        message="Are you sure you want to delete this session? This action cannot be undone."
        itemName={sessionToDelete?.client}
      />
      {showModal && (
        <CreateSession
          onSessionCreated={() => {
            setRefreshKey(prev => prev + 1);
            setShowModal(false);
            setEditingSession(null);
          }}
          editingSession={editingSession}
          onClose={() => {
            setShowModal(false);
            setEditingSession(null);
          }}
        />
      )}
    </motion.div>
  );
};

export default SessionCalendar;
